import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Grid, Select, MenuItem, FormControl, InputLabel, TextField, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import buxferService from '../services/buxfer.service';

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount || 0);
};

export default function SpendingReportPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('customer');
  const [year, setYear] = useState(new Date().getFullYear());
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await buxferService.getSpendingReport({ groupBy, year, page: 1, pageSize: 500, keyword });
        if (res && res.success) {
          const r = res.result;
          setGroups(r.groups || []);
        }
      } catch (e) { console.error('Failed to fetch spending report', e); }
      setLoading(false);
    };
    fetch();
  }, [groupBy, year, keyword]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Spending Report</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Group By</InputLabel>
              <Select value={groupBy} label="Group By" onChange={(e) => setGroupBy(e.target.value)}>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField size="small" label="Year" value={year} onChange={(e) => setYear(e.target.value)} />
          </Grid>
          <Grid item>
            <TextField size="small" placeholder="Search" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={() => { setKeyword(''); setYear(new Date().getFullYear()); setGroupBy('customer'); }}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        groups.map((g) => (
          <Accordion key={g.groupKey} defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>{g.groupLabel}</Typography>
                <Typography sx={{ color: 'gray' }}>YTD: {formatCurrency(g.groupTotals.ytd_spending)}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Card Open Date</TableCell>
                      <TableCell align="right">Current Balance</TableCell>
                      <TableCell align="right">YTD Spending</TableCell>
                      <TableCell align="right">{year}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.accounts.map(a => (
                      <TableRow key={a.id}>
                        <TableCell>{a.customer_name}</TableCell>
                        <TableCell>{a.name}</TableCell>
                        <TableCell>{a.open_date ? new Date(a.open_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(a.balance, a.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(a.ytd_spending)}</TableCell>
                        <TableCell align="right">{formatCurrency(a.spending_year)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
}
